import React, { useState, useEffect } from 'react';
import { X, Loader, Upload } from 'lucide-react';
import { peopleService } from './peopleService';
import { uploadService } from '../../lib/uploadService';
import { useAuth } from '../../hooks/useAuth';
import { Person } from '../../types';
import { SkillChipInput } from './SkillChipInput';

interface AddPersonModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const AddPersonModal: React.FC<AddPersonModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const { user } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [profileImage, setProfileImage] = useState<File | null>(null);
  const [profilePreview, setProfilePreview] = useState<string | null>(null);
  const [existingSkills, setExistingSkills] = useState<{ [category: string]: string[] }>({});
  
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    title: '',
    department: '',
    phone: '',
    bio: '',
    arbeidsoppgaver: '',
    verktoyOgSystemer: [] as string[],
    spesialiserteVerktoyUtvikler: [] as string[],
    spesialiserteVerktoyDesigner: [] as string[],
    spesialiserteVerktoyMarkedsforing: [] as string[],
    spesialiserteVerktoyProsjektledelse: [] as string[],
    spesialiserteVerktoy3D: [] as string[],
    spesialiserteVerktoyAI: [] as string[],
    andreFerdigheter: ''
  });
  
  const [error, setError] = useState('');

  // Load existing skills when modal opens
  useEffect(() => {
    if (isOpen) {
      loadExistingSkills();
    }
  }, [isOpen]);

  const loadExistingSkills = async () => {
    try {
      const people = await peopleService.getPeople();
      const skillCategories = {
        verktoyOgSystemer: new Set<string>(),
        spesialiserteVerktoyUtvikler: new Set<string>(),
        spesialiserteVerktoyDesigner: new Set<string>(),
        spesialiserteVerktoyMarkedsforing: new Set<string>(),
        spesialiserteVerktoyProsjektledelse: new Set<string>(),
        spesialiserteVerktoy3D: new Set<string>(),
        spesialiserteVerktoyAI: new Set<string>(),
      };

      people.forEach(person => {
        Object.keys(skillCategories).forEach(category => {
          const skills = person[category as keyof Person] as string[] || [];
          skills.forEach(skill => {
            if (skill.trim()) {
              skillCategories[category as keyof typeof skillCategories].add(skill.trim());
            }
          });
        });
      });

      const skillsSorted = Object.fromEntries(
        Object.entries(skillCategories).map(([category, skillSet]) => [
          category, 
          Array.from(skillSet).sort()
        ])
      );

      setExistingSkills(skillsSorted);
    } catch (error) {
      console.error('Failed to load existing skills:', error);
    }
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate the image file
    const validation = uploadService.validateProfileImage(file);
    if (!validation.valid) {
      setError(validation.error || 'Invalid image file');
      return;
    }

    setProfileImage(file);
    setError('');
    
    // Create preview URL
    const previewUrl = URL.createObjectURL(file);
    setProfilePreview(previewUrl);
  };

  const handleRemoveImage = () => {
    setProfileImage(null);
    setProfilePreview(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    if (!formData.email.trim() || !formData.name.trim()) {
      setError('Email and name are required');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      let profilePictureUrl = '';
      let profilePicturePath = '';

      // Upload profile image if selected
      if (profileImage) {
        try {
          console.log('Uploading profile image...');
          const uploadResult = await uploadService.uploadProfileImage(profileImage);
          profilePictureUrl = uploadResult.url;
          profilePicturePath = uploadResult.path;
          console.log('Profile image uploaded successfully:', profilePictureUrl);
        } catch (uploadError: any) {
          console.error('Failed to upload profile image:', uploadError);
          setError(`Failed to upload image: ${uploadError.message}`);
          setSubmitting(false);
          return;
        }
      }

      // Convert comma-separated strings to arrays for skills
      const skillsData = {
        verktoyOgSystemer: formData.verktoyOgSystemer,
        spesialiserteVerktoyUtvikler: formData.spesialiserteVerktoyUtvikler,
        spesialiserteVerktoyDesigner: formData.spesialiserteVerktoyDesigner,
        spesialiserteVerktoyMarkedsforing: formData.spesialiserteVerktoyMarkedsforing,
        spesialiserteVerktoyProsjektledelse: formData.spesialiserteVerktoyProsjektledelse,
        spesialiserteVerktoy3D: formData.spesialiserteVerktoy3D,
        spesialiserteVerktoyAI: formData.spesialiserteVerktoyAI,
      };

      await peopleService.addPerson({
        email: formData.email.trim(),
        name: formData.name.trim(),
        title: formData.title.trim(),
        department: formData.department.trim(),
        phone: formData.phone.trim(),
        profilePictureUrl,
        profilePicturePath,
        bio: formData.bio.trim(),
        arbeidsoppgaver: formData.arbeidsoppgaver.trim(),
        andreFerdigheter: formData.andreFerdigheter.trim(),
        ...skillsData
      });

      // Reset form and close modal
      setFormData({
        email: '', name: '', title: '', department: '', phone: '', bio: '',
        arbeidsoppgaver: '', verktoyOgSystemer: [], spesialiserteVerktoyUtvikler: [],
        spesialiserteVerktoyDesigner: [], spesialiserteVerktoyMarkedsforing: [],
        spesialiserteVerktoyProsjektledelse: [], spesialiserteVerktoy3D: [],
        spesialiserteVerktoyAI: [], andreFerdigheter: ''
      });
      setProfileImage(null);
      setProfilePreview(null);
      onSuccess();
      onClose();
    } catch (error: any) {
      setError(error.message || 'Failed to add person');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (submitting) return;
    setFormData({
      email: '', name: '', title: '', department: '', phone: '', bio: '',
      arbeidsoppgaver: '', verktoyOgSystemer: [], spesialiserteVerktoyUtvikler: [],
      spesialiserteVerktoyDesigner: [], spesialiserteVerktoyMarkedsforing: [],
      spesialiserteVerktoyProsjektledelse: [], spesialiserteVerktoy3D: [],
      spesialiserteVerktoyAI: [], andreFerdigheter: ''
    });
    setProfileImage(null);
    setProfilePreview(null);
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-background dark:bg-gray-800 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-xl">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold">Add Team Member</h2>
            <button
              onClick={handleClose}
              disabled={submitting}
              className="text-muted-foreground hover:text-foreground"
            >
              <X size={24} />
            </button>
          </div>

          {/* Error message */}
          {error && (
            <div className="bg-red-100 text-red-700 p-3 rounded mb-4">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Profile Image Upload */}
            <div>
              <label className="block text-sm font-medium mb-2">Profile Picture</label>
              <div className="flex items-center gap-4">
                <div 
                  onClick={() => document.getElementById('profile-image')?.click()}
                  className="w-20 h-20 rounded-full cursor-pointer hover:opacity-80 transition-opacity border-2 border-gray-200"
                >
                  {profilePreview ? (
                    <img
                      src={profilePreview}
                      alt="Profile preview"
                      className="w-20 h-20 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center transition-colors">
                      <Upload size={24} className="text-gray-400" />
                    </div>
                  )}
                </div>
                
                <div>
                  <input
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    onChange={handleImageSelect}
                    className="hidden"
                    id="profile-image"
                  />
                  <button
                    type="button"
                    onClick={() => document.getElementById('profile-image')?.click()}
                    className="bg-secondary text-secondary-foreground px-4 py-2 rounded-lg hover:bg-secondary/80 transition-colors border"
                  >
                    Choose Image
                  </button>
                  <p className="text-xs text-muted-foreground mt-1">
                    Optional. JPG, PNG, or WebP up to 5MB. Click avatar or button to select.
                  </p>
                </div>
              </div>
            </div>

            {/* Basic Information */}
            <div className="space-y-4">
              {/* Full Name - Full width, first */}
              <div>
                <label className="block text-sm font-medium mb-2">Full Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full p-3 border rounded-lg"
                  placeholder="John Doe"
                  required
                />
              </div>

              {/* Job Title and Department */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Job Title</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full p-3 border rounded-lg"
                    placeholder="Senior Designer"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Department</label>
                  <input
                    type="text"
                    value={formData.department}
                    onChange={(e) => setFormData(prev => ({ ...prev, department: e.target.value }))}
                    className="w-full p-3 border rounded-lg"
                    placeholder="Oslo, Tønsberg, Stavanger"
                  />
                </div>
              </div>

              {/* Email and Phone */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full p-3 border rounded-lg"
                    placeholder="employee@agency.com"
                    required
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    This will link to their user account when they register
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Phone</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    className="w-full p-3 border rounded-lg"
                    placeholder="+47 123 45 678"
                  />
                </div>
              </div>
            </div>

            {/* Bio */}
            <div>
              <label className="block text-sm font-medium mb-2">Bio</label>
              <textarea
                value={formData.bio}
                onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value }))}
                className="w-full p-3 border rounded-lg h-24"
                placeholder="Brief professional bio..."
              />
            </div>

            {/* Skills Sections */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Skills & Tools</h3>
              
              <div>
                <label className="block text-sm font-medium mb-2">Work Tasks</label>
                <textarea
                  value={formData.arbeidsoppgaver}
                  onChange={(e) => setFormData(prev => ({ ...prev, arbeidsoppgaver: e.target.value }))}
                  className="w-full p-3 border rounded-lg h-20"
                  placeholder="Describe main work tasks and responsibilities..."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <SkillChipInput
                  label="Tools and Systems"
                  skills={formData.verktoyOgSystemer}
                  onSkillsChange={(skills) => setFormData(prev => ({ ...prev, verktoyOgSystemer: skills }))}
                  existingSkills={existingSkills.verktoyOgSystemer || []}
                  placeholder="Slack, Google Workspace, etc."
                />

                <SkillChipInput
                  label="Specialized Tools: Developer"
                  skills={formData.spesialiserteVerktoyUtvikler}
                  onSkillsChange={(skills) => setFormData(prev => ({ ...prev, spesialiserteVerktoyUtvikler: skills }))}
                  existingSkills={existingSkills.spesialiserteVerktoyUtvikler || []}
                  placeholder="React, Node.js, Python, etc."
                />

                <SkillChipInput
                  label="Specialized Tools: Designer"
                  skills={formData.spesialiserteVerktoyDesigner}
                  onSkillsChange={(skills) => setFormData(prev => ({ ...prev, spesialiserteVerktoyDesigner: skills }))}
                  existingSkills={existingSkills.spesialiserteVerktoyDesigner || []}
                  placeholder="Figma, Adobe Creative Suite, etc."
                />

                <SkillChipInput
                  label="Specialized Tools: Marketing"
                  skills={formData.spesialiserteVerktoyMarkedsforing}
                  onSkillsChange={(skills) => setFormData(prev => ({ ...prev, spesialiserteVerktoyMarkedsforing: skills }))}
                  existingSkills={existingSkills.spesialiserteVerktoyMarkedsforing || []}
                  placeholder="Google Ads, Facebook Ads, etc."
                />

                <SkillChipInput
                  label="Specialized Tools: Project Management"
                  skills={formData.spesialiserteVerktoyProsjektledelse}
                  onSkillsChange={(skills) => setFormData(prev => ({ ...prev, spesialiserteVerktoyProsjektledelse: skills }))}
                  existingSkills={existingSkills.spesialiserteVerktoyProsjektledelse || []}
                  placeholder="Asana, Jira, etc."
                />

                <SkillChipInput
                  label="Specialized Tools: 3D"
                  skills={formData.spesialiserteVerktoy3D}
                  onSkillsChange={(skills) => setFormData(prev => ({ ...prev, spesialiserteVerktoy3D: skills }))}
                  existingSkills={existingSkills.spesialiserteVerktoy3D || []}
                  placeholder="Blender, Cinema 4D, etc."
                />

                <SkillChipInput
                  label="Specialized Tools: AI"
                  skills={formData.spesialiserteVerktoyAI}
                  onSkillsChange={(skills) => setFormData(prev => ({ ...prev, spesialiserteVerktoyAI: skills }))}
                  existingSkills={existingSkills.spesialiserteVerktoyAI || []}
                  placeholder="ChatGPT, Midjourney, etc."
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Other Skills</label>
                <textarea
                  value={formData.andreFerdigheter}
                  onChange={(e) => setFormData(prev => ({ ...prev, andreFerdigheter: e.target.value }))}
                  className="w-full p-3 border rounded-lg h-20"
                  placeholder="Other skills, certifications, languages, etc."
                />
              </div>
            </div>

            {/* Buttons */}
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                disabled={submitting}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-accent disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!formData.email.trim() || !formData.name.trim() || submitting}
                className="flex-1 bg-primary text-primary-foreground px-4 py-2 rounded-lg border border-primary flex items-center justify-center gap-2 disabled:opacity-50 hover:bg-primary/90"
              >
                {submitting ? (
                  <>
                    <Loader className="animate-spin" size={16} />
                    {profileImage ? 'Uploading image...' : 'Adding...'}
                  </>
                ) : (
                  'Add Team Member'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};